import { motion } from 'framer-motion';
import { 
  Clock, MapPin, Users, BookOpen, AlertCircle, Car,
  CheckCircle, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';
import LaserBorder from '../components/LaserBorder';

const etiquette = [
  'Arrive early to find your place before the khutbah begins',
  'Enter quietly and avoid stepping over people',
  'Turn off or silence your mobile phone',
  'Listen attentively during the khutbah',
  'Avoid talking during the khutbah',
  'Straighten the rows before prayer',
  'Park considerately for our neighbors'
];

const JummahPage = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Friday Prayer
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Weekly <span className="text-gradient">Jummah Prayer</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Join us every Friday for Jummah prayers. All Muslims are welcome to attend and be part of our growing community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Prayer Times */}
      <section className="py-24 lg:py-32" data-testid="jummah-times">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <SectionHeading
                align="left"
                badge="Prayer Schedule"
                title="Join Us Every Friday"
                description="Our weekly Jummah service is the heart of our community gathering."
              />

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <BookOpen className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Khutbah (Sermon)</p>
                    <p className="text-2xl font-bold font-['Outfit'] text-foreground prayer-display">1:30 PM</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border">
                  <div className="p-3 rounded-xl bg-secondary/10">
                    <Clock className="h-6 w-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Salat (Prayer)</p>
                    <p className="text-2xl font-bold font-['Outfit'] text-foreground prayer-display">1:50 PM</p>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground">
                <AlertCircle className="inline h-4 w-4 mr-2 text-secondary" />
                Times may vary during summer and winter months. Check our WhatsApp for updates.
              </p>

              <a 
                href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="jummah-whatsapp-btn"
              >
                <Button className="rounded-full bg-green-500 hover:bg-green-600 text-white">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Join WhatsApp for Updates
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <LaserBorder>
                <div className="p-8 space-y-6" data-testid="jummah-location-card">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-primary/10">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Location</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-accent/50">
                      <p className="font-medium text-foreground">54 St. Paul Crescent</p>
                      <p className="text-muted-foreground">Barrie, ON L9J 0M2</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-accent/50 text-center">
                        <Users className="h-5 w-5 text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Separate Sections</p>
                        <p className="text-xs text-muted-foreground">Men & Women</p>
                      </div>
                      <div className="p-4 rounded-xl bg-accent/50 text-center">
                        <Car className="h-5 w-5 text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Street Parking</p>
                        <p className="text-xs text-muted-foreground">Available</p>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      Washrooms available. Please arrive early to find parking and settle in before khutbah.
                    </p>
                  </div>

                  <a 
                    href="https://maps.google.com/?q=54+St+Paul+Crescent+Barrie+ON+L9J+0M2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="w-full rounded-xl">
                      <MapPin className="mr-2 h-4 w-4" />
                      Get Directions
                    </Button>
                  </a>
                </div>
              </LaserBorder>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Etiquette */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="jummah-etiquette">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Guidelines"
            title="Jummah Etiquette"
            description="Help us maintain a peaceful and focused environment for everyone."
          />

          <div className="max-w-2xl mx-auto">
            <div className="space-y-4">
              {etiquette.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-background border border-border"
                >
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* First Time */}
      <section className="py-24 lg:py-32" data-testid="first-time">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <SectionHeading
              badge="New Here?"
              title="First Time at Jummah?"
              description="We welcome everyone! Here's what to expect."
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-card border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">1</div>
                <h4 className="font-semibold text-foreground mb-2">Arrive Early</h4>
                <p className="text-sm text-muted-foreground">Come by 1:15 PM to find parking and settle in</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">2</div>
                <h4 className="font-semibold text-foreground mb-2">Find Your Space</h4>
                <p className="text-sm text-muted-foreground">Separate prayer areas for men and women</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">3</div>
                <h4 className="font-semibold text-foreground mb-2">Join the Prayer</h4>
                <p className="text-sm text-muted-foreground">Follow along - our community will help guide you</p>
              </div>
            </div>

            <p className="text-muted-foreground">
              Questions? Feel free to approach any community member - we're here to help!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JummahPage;
